'use client';

import dynamic from 'next/dynamic';
import { useTranslations } from 'next-intl';
import { Heading, Modal, Text } from '@zillow/constellation-10';
import { interactionResponse } from '@zillow/yield-callback';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api/endpoints/area';
import type { FilterTrackingContext } from './filterAnalytics';
import type { FilterState } from '@/src/lib/filters/filterTypes';

export type AllFiltersModalProps = {
  trackingContext?: FilterTrackingContext | null;
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  setFilters: (next: FilterState) => void;
  /**
   * Area the price histogram is scoped to. Omitted by the shell-hosted copy on
   * the SRP, where the current area already is the scope; passed by the
   * navigate-mode copy, whose staged area can differ from it.
   */
  area?: AreaResponse | AutocompleteResult | null;
  /** Render the Beds/Baths sections (true when Rooms is not inline in the bar). */
  showRoomsSection: boolean;
  /** Render the Price section (true when Price is not inline in the bar). */
  showPriceSection: boolean;
};

const AllFiltersModalContents = dynamic(() => import('./AllFiltersModalContents'), {
  ssr: false,
  loading: () => <FiltersLoading />,
});

function FiltersLoading() {
  const t = useTranslations('common');
  return (
    <Modal.Body>
      <Text role="status">{t('loading')}</Text>
    </Modal.Body>
  );
}

export default function AllFiltersModal(props: AllFiltersModalProps) {
  const t = useTranslations('filterModal');
  return (
    <Modal.Root
      open={props.isOpen}
      onOpenChange={async (open, _event, reason) => {
        if (open) return;
        // C11n 10.18.1 yields for dismissals, but Modal.CloseButton reports "click" without yielding.
        if (reason === 'click') await interactionResponse();
        props.onClose();
      }}
      size={{ base: 'fullScreen', lg: 'md' }}
    >
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-testid="listing-search-all-filters">
            <Modal.Header>
              <Heading level={2}>{t('filters')}</Heading>
            </Modal.Header>
            <AllFiltersModalContents {...props} />
            <Modal.CloseButton data-testid="filter-close" />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
}
